import { useState } from "react";
import * as Sentry from "@sentry/react";

import { useSnackbar } from "globals/hooks";
import { NotificationVariable } from "types";

import useNotificationVariables from "./useNotificationVariables";

type UseSmartKeysMenuProps = {
  availableVariables: string[];
};

type UseSmartKeysMenuReturn = {
  menuProps: {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
  };
  buttonProps: {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled: boolean;
  };
  handleCopyToClipboard: (value: string) => Promise<void>;
  notificationVariables: NotificationVariable[];
  loading: boolean;
};

export function useSmartKeysMenu({
  availableVariables,
}: UseSmartKeysMenuProps): UseSmartKeysMenuReturn {
  // hooks
  const snackbar = useSnackbar();
  const { notificationVariables, loading } = useNotificationVariables(
    availableVariables
  );

  // state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // event handlers
  const handleOpenSmartKeysMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSmartKeysMenu = () => {
    setAnchorEl(null);
  };

  const handleCopyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(`{${value}}`);
      snackbar.success(`Copied {${value}} to clipboard`);
      handleCloseSmartKeysMenu();
    } catch (err) {
      Sentry.captureException(err);
      snackbar.error(
        "Failed to copy smart key. Please type out smart key directly."
      );
    }
  };

  return {
    menuProps: {
      anchorEl,
      open,
      onClose: handleCloseSmartKeysMenu,
    },
    buttonProps: {
      onClick: handleOpenSmartKeysMenu,
      disabled: loading,
    },
    handleCopyToClipboard,
    notificationVariables,
    loading,
  };
}